import Vue from 'vue';
import UserForm from '@/tools/models/UserForm';
import { mask } from 'vue-the-mask';
import ApiRequest from '@/tools/ApiRequest';
import formValidationRules from '@/tools/validationRules/formValidationRules';
import TMAlert from '@/tools/models/TMAlert';
import UsageTerms from '@/components/UsageTerms.vue';
import textFieldDefaultProps from '@/tools/helpers/textFieldDefaultProps';
import RequestException from '@/tools/helpers/RequestException';

export default Vue.extend({
  components: {
    'usage-terms': UsageTerms,
  },
  directives: {
    mask,
  },
  data: () => ({
    textFieldProps: textFieldDefaultProps,
    /**
     * Define quais compos do formulário serão mostrados em tela
     *
     * @defaultValue 1
     */
    formPart: 1 as 1 | 2 | 3,
    /**
     * Variável relacionada à validação do formulário. Se todos os campos forem válidos é atribuido
     * true à ela.
     *
     * @defaultValue false
     */
    isFormValid: false as boolean,
    /**
     * Dados inseridos no formulário. Armazena todos os dados necessários para a submissão do
     * cadastro
     *
     * @defaultValue new ResgisterForm()
     */
    formData: new UserForm() as UserForm,
    /**
     * Informações do estado selecionado (obtido através do CEP)
     *
     * @defaultValue { id: -1, title: '' }
     */
    selectedState: {
      id: -1,
      title: '',
    } as { id: number; title: string },
    /**
     * Informações da cidade selecionada (obtido através do CEP)
     *
     * @defaultValue { id: -1, title: '' }
     */
    selectedCity: {
      id: -1,
      title: '',
    } as { id: number; title: string },
    /**
     * Variável que define qual o tipo de pessoa (jurédica ou física) está sendo cadastrada.
     * Deve ser 'cpf' ou 'cnpj'.
     *
     * @defaultValue 'cpf'
     */
    documentType: 'cpf' as 'cpf' | 'cnpj',
    /**
     * Resposta dada pelo usuário na última parde do cadastro.
     *
     * @defaultValue ''
     */
    resposta: '' as string,
    /**
     * Senha atual.
     * Necessária caso o usuário queira alterar sua senha.
     *
     * @defaultValue ''
     */
    currentPassword: '' as string,
    /**
     * Regras para a validação dos campos.
     *
     * Importado de src/tools/validationRules/formValidationRules
     */
    validationRules: formValidationRules,
  }),
  computed: {
    saveBtnText(): string {
      return this.formPart === 1 ? 'CONTINUAR' : 'CADASTRAR';
    },
    /**
     * Validação da confirmação de senha.
     * Necessário que esteja no componente pois precisa ter acesso ao this.formaData.password para a
     * comparação.
     */
    confirmPasswordValidationRules(): any[] {
      return [
        this.validationRules.mandatory,
        (v: any) => v === this.formData.password || 'Confirmação de senha deve ser igual à senha',
      ];
    },
    /**
     * Define que o formulário possui os métodos validate e resetValidation.
     * Necessário para as definições de tipos do TypeScript.
     */
    userForm(): Vue & { validate: () => boolean; resetValidation: () => boolean } {
      return this.$refs.userForm as Vue & {
        validate: () => boolean;
        resetValidation: () => boolean;
      };
    },
    /**
     * Define que o componente de temros de uso possui o método open.
     * Necessário para as definições de tipos do TypeScript.
     */
    usageTerms(): Vue & { open: () => void } {
      return this.$refs.usageTerms as Vue & { open: () => void };
    },
  },
  methods: {
    /**
     * Faz a requição ao backend para obter dados do endereço por meio do CEP.
     */
    getAddressByZipcode(): void {
      /**
       * Faz a requisição apenas quando o cep inteiro foi digitado
       */
      if (this.formData.zipcode.length === 9) {
        ApiRequest.checkZipcode(this.formData.zipcode)
          .then((response) => {
            /**
             * Em caso de sucesso preenche os campos com os dados obtidos..
             */
            this.formData.address = response.data.address;
            this.formData.neighborhood = response.data.neighborhood;
            this.formData.state_id = response.data.state_id;
            this.formData.city_id = response.data.city_id;
            this.selectedState.id = response.data.state_id;
            this.selectedState.title = response.data.state;
            this.selectedCity.id = response.data.city_id;
            this.selectedCity.title = response.data.city;
          })
          .catch((error) => new RequestException(error, this.$store, this.$router));
      }
    },
    /**
     * Função do botão de cancelar/voltar. Redireciona para o login em caso de cancelamento ou
     * retorna à arte anterior do formulário caso seja clicado em votltar.
     */
    backOrCancel(): void {
      if (this.formPart === 1) {
        this.$router.push('/login');
      } else {
        this.formPart -= 1;
      }
    },
    /**
     * Função do botão avançar/salvar. Avança para a próxima parte do formulário ou chama a função
     * de cadastro caso esteja na ultima parte do formulário.
     */
    nextOrSave(): void {
      /**
       * Faz a validação do formulário
       */
      if (this.userForm.validate()) {
        /**
         * Avança para a próxima parte do formulário caso esteja na primeira ou segunda parte.
         */
        if (this.formPart === 1) {
          this.formPart += 1;
        } else {
          /**
           * Verifica se a resposta da última parte do formulário está correta. Caso esteja chama a
           * função de registro. Caso contrário exibe uma mensagem de erro.
           */
          if (this.resposta.toLocaleLowerCase().includes('nortox')) {
            this.register();
          } else {
            this.$store.dispatch(
              'setAlert',
              new TMAlert({
                title: 'Resposta incorreta',
                message: 'Verifique sua resposta e tente novamente.',
                state: true,
              }),
            );
          }
        }
      } else if (this.formData.password !== this.formData.confirmPassword) {
        this.$store.dispatch(
          'setAlert',
          new TMAlert({
            title: 'Confirmação de senha',
            message: "Os campos 'Senha' e 'Repetir senha' devem ser iguais",
            state: true,
          }),
        );
      }
    },
    /**
     * Faz a requisição de registro de usuário.
     */
    register(): void {
      ApiRequest.register(this.formData)
        .then((response) => {
          this.$store.dispatch(
            'setAlert',
            new TMAlert({
              title: 'Sucesso!',
              message: 'Cadastro realizado com sucesso.',
              state: true,
            }),
          );

          this.$router.push('/login');
        })
        .catch((error) => new RequestException(error, this.$store, this.$router));
    },
    /**
     * Abre o dialog de Termos de Uso
     */
    openUsageTerms(): void {
      this.usageTerms.open();
    },
  },
});
